import { fromBase64, toBase64 } from "./base64";

/**
 * What a pairing QR code / typed code carries. It contains NO private key:
 *  - the computer's public key (the phone pins it, so a relay can't impersonate the computer),
 *  - the relay to connect through,
 *  - a one-time pairing token (single use, short-lived) proving the user saw this code.
 * Binary layout: version(1) | daemon public key(32) | token(16) | expiresAt unix seconds(4, BE) | relay URL (utf-8).
 */
export interface PairingPayload {
  relayUrl: string;
  daemonPublicKey: Uint8Array;
  token: Uint8Array;
  /** Unix seconds. */
  expiresAt: number;
  /**
   * The computer's name ("Studio Mac"), when the link or QR text carried one (`...#pair=FM1.xxxx&name=Studio%20Mac`). It is not part of
   * the code itself, so older apps read the same links unchanged; the phone offers it as the name for this computer.
   */
  name?: string;
}

const VERSION = 1;
const PREFIX = "FM1.";
export const PAIRING_TOKEN_BYTES = 16;

const b64url = (b: Uint8Array) => toBase64(b).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const unb64url = (s: string) => {
  const std = s.replace(/-/g, "+").replace(/_/g, "/");
  return fromBase64(std + "=".repeat((4 - (std.length % 4)) % 4));
};

/** Appends the computer's name to a pairing link: `<link>&name=Studio%20Mac`. */
export function withComputerName(link: string, name: string | undefined | null): string {
  const n = (name ?? "").trim().slice(0, 64);
  return n && link.includes("#pair=") ? `${link}&name=${encodeURIComponent(n)}` : link;
}

/** The name carried next to a pairing code in a link, if any (letters, digits and simple punctuation only; never trusted as markup). */
function nameFromText(text: string): string | undefined {
  const m = /[#&?]name=([^&#\s]{1,200})/.exec(text);
  if (!m) return undefined;
  try {
    const n = decodeURIComponent(m[1]!).replace(/[\u0000-\u001f\u007f]/g, "").trim().slice(0, 64);
    return n || undefined;
  } catch {
    return undefined;
  }
}

export function tokenToString(token: Uint8Array): string {
  return b64url(token);
}
export function tokenFromString(s: string): Uint8Array {
  return unb64url(s);
}

export function encodePairingCode(p: PairingPayload): string {
  if (p.daemonPublicKey.length !== 32) throw new Error("daemon public key must be 32 bytes");
  if (p.token.length !== PAIRING_TOKEN_BYTES) throw new Error("token must be 16 bytes");
  const relay = new TextEncoder().encode(p.relayUrl);
  const out = new Uint8Array(1 + 32 + 16 + 4 + relay.length);
  out[0] = VERSION;
  out.set(p.daemonPublicKey, 1);
  out.set(p.token, 33);
  new DataView(out.buffer).setUint32(49, p.expiresAt);
  out.set(relay, 53);
  return PREFIX + b64url(out);
}

/**
 * Accepts the bare code or any text/URL containing it (e.g. `https://app.example/#pair=FM1.xxxx`,
 * which is what a QR opened from a phone camera would hold). Throws on anything malformed.
 */
export function decodePairingCode(input: string): PairingPayload {
  const match = /FM1\.([A-Za-z0-9_-]+)/.exec(input.trim());
  if (!match) throw new Error("not a pairing code");
  let bytes: Uint8Array;
  try {
    bytes = unb64url(match[1]!);
  } catch {
    throw new Error("pairing code is corrupted");
  }
  if (bytes.length < 54 || bytes[0] !== VERSION) throw new Error("unsupported or corrupted pairing code");
  const relayUrl = new TextDecoder("utf-8", { fatal: true, ignoreBOM: false }).decode(bytes.subarray(53));
  const name = nameFromText(input);
  return {
    daemonPublicKey: bytes.slice(1, 33),
    token: bytes.slice(33, 49),
    expiresAt: new DataView(bytes.buffer, bytes.byteOffset).getUint32(49),
    relayUrl,
    ...(name ? { name } : {}),
  };
}

export function isPairingExpired(p: PairingPayload, nowMs = Date.now()): boolean {
  return nowMs / 1000 >= p.expiresAt;
}
